import classNames from 'classnames';
import React from 'react';

import * as ArchivePurposesUtil from 'archive/ArchivePurposesUtil';
import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import {assertAndContinue} from 'Asserts';
import * as benchlingUtil from 'BenchlingUtil';
import * as contextmenu from 'components/ContextMenu';
import {WorkspaceItem} from 'constants/FileBrowserConstants';
import FileBrowserUtil from 'file-browser/FileBrowserUtil';
import FileState from 'filestate/FileState';
import {ReviewStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isFailed} from 'schemas/SchemaValidationChecks';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as mouse from 'util/Mouse';
import {ElementMouseEvent} from 'util/Types';

import {ClearWorkspaceActionType} from './WorkspaceConstants';

export interface WorkspaceItemTabProps {
  item: WorkspaceItem;
  isActive: boolean;
  isOverflowTab: boolean;
  isLastTab: boolean;
  // For non-dragged overhead tabs, set to true to indicate the tab should use the smallest
  // allowed width (because there are overflow tabs).
  forceMinWidth: boolean;

  // When this component is a dragged tab, it won't have any callbacks or the index since it can't
  // be interacted with (aside from moving the mouse to drag).
  openFile?: (fileId: string, params?: OpenFileParams) => void;
  onClearWorkspace?: (action: ClearWorkspaceActionType, itemId?: string) => void;
  onDragStart?: (e: ElementMouseEvent, index: number, tabWidth: number) => void;
  index?: number;

  // When dragging a tab, the normal tab becomes an invisible placeholder with isInvisible set,
  // and a "fake" tab is drawn in the right place using dragLayoutParams.
  isInvisible: boolean;
  dragLayoutParams?: {
    translateX: number;
    width: number;
  };
}

export interface OpenFileParams {
  onOpen: (fileState: FileState | undefined) => void;
}

interface State {
  isCloseButtonVisible: boolean;
}

class WorkspaceItemTab extends React.Component<WorkspaceItemTabProps, State> {
  static defaultProps = {
    isInvisible: false,
  };

  state: State = {
    isCloseButtonVisible: false,
  };

  refs!: {
    name: HTMLElement;
    closeBtn: HTMLButtonElement;
  };
  tabRef = React.createRef<HTMLDivElement>();

  _handleContextMenu = (e: ElementMouseEvent): void => {
    const menu: Array<contextmenu.ContextMenuOption> = [
      {
        name: 'Close',
        click: () => {
          if (this.props.onClearWorkspace) {
            this.props.onClearWorkspace(ClearWorkspaceActionType.CLOSE_SELF, this.props.item.fileId);
          }
        },
      },
      {
        name: 'Close Other Tabs',
        click: () => {
          if (this.props.onClearWorkspace) {
            this.props.onClearWorkspace(ClearWorkspaceActionType.CLOSE_OTHERS, this.props.item.fileId);
          }
        },
        disabled: this.props.index === 0 && this.props.isLastTab,
      },
      {
        name: 'Close Tabs to the Right',
        click: () => {
          if (this.props.onClearWorkspace) {
            this.props.onClearWorkspace(ClearWorkspaceActionType.CLOSE_RIGHT, this.props.item.fileId);
          }
        },
        disabled: this.props.isLastTab,
      },
      '-',
      {
        name: 'Move to New Browser Tab',
        click: () => {
          this._handleOpenNewBrowserTab();
        },
      },
    ];
    contextmenu.view.open(e.nativeEvent, menu, true /* raiseZIndex */);
  };

  _handleOpenNewBrowserTab = (): void => {
    if (this.props.openFile) {
      this.props.openFile(this.props.item.fileId, {
        onOpen: (fileState: FileState) => {
          if (fileState) {
            window.open(fileState.editURL(), '_blank');
          }
          // Only close the target tab if there was an openFile handler called
          this._handleCloseSelf();
        },
      });
    }
  };

  _handleCloseSelf = (): void => {
    if (this.props.onClearWorkspace) {
      this.props.onClearWorkspace(ClearWorkspaceActionType.CLOSE_SELF, this.props.item.fileId);
    }
  };

  _handleCloseMouseDown = (e: ElementMouseEvent): void => {
    // Don't let drag operations start on the close button.
    e.stopPropagation();
  };

  _handleMouseEnter = (): void => {
    this.setState({
      isCloseButtonVisible: true,
    });
  };

  _handleMouseMove = (): void => {
    this.setState({isCloseButtonVisible: true});
  };

  _handleMouseLeave = (): void => {
    this.setState({isCloseButtonVisible: false});
  };

  _handleMouseDown = (e: ElementMouseEvent): void => {
    if (mouse.isMiddleClick(e)) {
      this._handleCloseSelf();
    } else if (benchlingUtil.isSimpleClick(e)) {
      const {index, onDragStart} = this.props;
      if (!assertAndContinue(index != null, 'Cannot start dragging on a dragged tab.')) {
        return;
      }
      const tabRect = this.tabRef.current!.getBoundingClientRect();
      if (onDragStart && index != null) {
        onDragStart(e, index, tabRect.width);
      }
    }
  };

  _renderIcon(item: WorkspaceItem): JSX.Element {
    if (isFailed(item.schemaValidationState)) {
      return <BenchlingIcon icon={ReviewStatus.REJECTED} className="u-danger-color" />;
    }
    return <BenchlingIcon icon={FileBrowserUtil.getFileIconName(item, 'fileId')} />;
  }

  render(): JSX.Element {
    const {dragLayoutParams, forceMinWidth, isActive, item, isInvisible, isOverflowTab} = this.props;
    const isDragging = dragLayoutParams != null;

    let style = {};
    if (isDragging) {
      // When this component is a "fake" dragged tab, it is absolutely positioned rather than having
      // its width and position specified by the flexbox, so the width and position need to be given
      // as parameters. The position is based on the mouse position, and the width was measured at
      // the start of the drag operation.
      style = {
        transform: `translateX(${dragLayoutParams.translateX}px)`,
        width: dragLayoutParams.width,
      };
    }
    return (
      <Tooltip
        container="body"
        placement={isOverflowTab ? TooltipPlacement.LEFT : TooltipPlacement.BOTTOM}
        title={ArchivePurposesUtil.getArchiveItemName({...item, id: item.fileId})}
        disabled={isInvisible || isDragging}
        delay={{show: 250}}
      >
        <div
          className={classNames('workspace-itemTab', {
            'is-active': isActive,
            'is-dragging': isDragging,
            'is-invisible': isInvisible,
            'workspace-itemTab--overhead': !isOverflowTab,
            'workspace-itemTab--overflow': isOverflowTab,
            'workspace-itemTab--faded':
              Boolean(item.archiveRecord) || (isFailed(item.schemaValidationState) && !isActive),
            'is-min-width': forceMinWidth,
            'is-failed': isFailed(item.schemaValidationState),
          })}
          onMouseDown={isDragging ? undefined : this._handleMouseDown}
          onMouseEnter={isDragging ? undefined : this._handleMouseEnter}
          onMouseLeave={isDragging ? undefined : this._handleMouseLeave}
          onMouseMove={isDragging ? undefined : this._handleMouseMove}
          onContextMenu={isDragging ? undefined : this._handleContextMenu}
          style={style}
          ref={this.tabRef}
          {...getTestProps({component: 'WorkspaceItemTab', element: 'root'})}
        >
          <div className="workspace-itemTab-icon">{this._renderIcon(item)}</div>
          <div
            className="workspace-itemTab-text"
            {...getTestProps({component: 'WorkspaceItemTab', element: 'text'})}
          >
            {item.name}
            <ArchiveRecordLabel item={{...item, id: item.fileId}} />
          </div>
          {(isActive || this.state.isCloseButtonVisible) && (
            <div
              className="workspace-itemTab-close"
              onClick={isDragging ? undefined : this._handleCloseSelf}
              onMouseDown={isDragging ? undefined : this._handleCloseMouseDown}
            >
              <BenchlingIcon dataTestAttrs={{component: 'WorkspaceItemTab', key: 'close'}} icon="close" />
            </div>
          )}
        </div>
      </Tooltip>
    );
  }
}

export default WorkspaceItemTab;
